import { Calendar, FileText, Activity, Phone, MessageSquare } from "lucide-react";
import SMSConsentForm from "@/components/SMSConsentForm";

const HERO_IMAGE = "https://images.unsplash.com/photo-1649751361457-01d3a696c7e6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxwaHlzaW90aGVyYXBpc3QlMjBzdHJldGNoaW5nJTIwcGF0aWVudCUyMGxlZyUyMGNsaW5pY3xlbnwwfHx8fDE3NzM3MTU3MDF8MA&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  {
    icon: Calendar,
    title: "Appointment Reminders",
    description: "Get timely reminders so you do not miss your scheduled visit.",
  },
  {
    icon: FileText,
    title: "Booking Confirmations",
    description: "Receive confirmation and follow-up updates related to your appointment request.",
  },
  {
    icon: Activity,
    title: "Care Updates",
    description: "Stay informed about service-related updates and clinic communication connected to your care.",
  },
];

export default function SMSConsentPage() {
  return (
    <div className="min-h-screen" data-testid="sms-consent-page">
      {/* Hero Section */}
      <section className="relative bg-slate-50 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div 
            className="absolute inset-0" 
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%230047AB' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 lg:py-24 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Column - Content */}
            <div className="order-2 lg:order-1">
              <div className="inline-flex items-center space-x-2 bg-[#0047AB]/10 text-[#0047AB] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <MessageSquare className="h-4 w-4" />
                <span>SMS Preferences & Appointment Requests</span>
              </div>
              
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-6 leading-tight">
                Patient Care, Appointment Updates, and{" "}
                <span className="text-[#0047AB]">Easy Booking</span>
              </h1>
              
              <p className="text-lg text-slate-600 mb-8 leading-relaxed max-w-xl">
                Connect with Muscle & Movement Rehab for physiotherapy, chiropractic, massage therapy, 
                naturopathic care, fitness support, and more. Use this page to request an appointment 
                and choose whether you want text updates about your care.
              </p>

              {/* Trust Indicators */}
              <div className="flex flex-col gap-3 mb-8">
                <div className="flex items-center space-x-2 text-slate-600">
                  <div className="w-2 h-2 bg-[#7FFF00] rounded-full"></div>
                  <span className="text-sm">Patient-focused care</span>
                </div>
                <div className="flex items-center space-x-2 text-slate-600">
                  <div className="w-2 h-2 bg-[#7FFF00] rounded-full"></div>
                  <span className="text-sm">Individualized treatment plans</span>
                </div>
                <div className="flex items-center space-x-2 text-slate-600">
                  <div className="w-2 h-2 bg-[#7FFF00] rounded-full"></div>
                  <span className="text-sm">Evidence-based approach</span>
                </div>
                <div className="flex items-center space-x-2 text-slate-600">
                  <div className="w-2 h-2 bg-[#7FFF00] rounded-full"></div>
                  <span className="text-sm">Direct billing available</span>
                </div>
                <div className="flex items-center space-x-2 text-slate-600">
                  <div className="w-2 h-2 bg-[#7FFF00] rounded-full"></div>
                  <span className="text-sm">Friendly, experienced team</span>
                </div>
              </div>

              {/* Contact Quick Info */}
              <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm inline-flex items-center space-x-4">
                <div className="w-12 h-12 bg-[#0047AB]/10 rounded-full flex items-center justify-center">
                  <Phone className="h-5 w-5 text-[#0047AB]" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Questions? Call us</p>
                  <a 
                    href="tel:+16474903782" 
                    className="font-semibold text-slate-800 hover:text-[#0047AB] transition-colors"
                    data-testid="hero-phone-link"
                  >
                    +1 647-490-3782
                  </a>
                </div>
              </div>
            </div>

            {/* Right Column - Form */}
            <div className="order-1 lg:order-2">
              <SMSConsentForm />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-semibold text-slate-900 mb-4">
              Why Patients Choose Muscle & Movement Rehab
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We make it easier to stay on top of your care with convenient appointment coordination, 
              booking confirmations, and service updates, while delivering hands-on, individualized 
              treatment in clinic.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {FEATURES.map((feature, index) => (
              <div 
                key={index}
                className="feature-card bg-slate-50 rounded-xl p-6 border border-slate-100"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 bg-[#0047AB] rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-800 mb-2">
                  {feature.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src={HERO_IMAGE} 
                alt="Professional physiotherapy treatment at Muscle & Movement Rehab"
                className="rounded-2xl shadow-lg w-full h-auto object-cover aspect-[4/3]"
                data-testid="hero-image"
              />
              <div className="absolute -bottom-4 -right-4 bg-white rounded-xl p-4 shadow-lg hidden md:block">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-[#7FFF00] rounded-full flex items-center justify-center">
                    <MessageSquare className="h-5 w-5 text-slate-800" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800 text-sm">SMS Optional</p>
                    <p className="text-xs text-slate-500">Only if you choose to opt in</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="font-heading text-3xl font-semibold text-slate-900 mb-6">
                Care That Supports Your Recovery
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                At Muscle & Movement Rehab, our team provides individualized care designed to help 
                patients move better, recover faster, and perform at their best. Whether you are 
                booking physiotherapy, chiropractic care, massage therapy, naturopathic care, or 
                fitness support, our clinic is committed to a professional and patient-first experience.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-[#7FFF00] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-slate-800">✓</span>
                  </div>
                  <p className="text-slate-600">
                    Physiotherapy, chiropractic, massage therapy, naturopathic care, and fitness support
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-[#7FFF00] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-slate-800">✓</span>
                  </div>
                  <p className="text-slate-600">
                    Individualized treatment plans based on your needs
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-[#7FFF00] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-slate-800">✓</span>
                  </div>
                  <p className="text-slate-600">
                    Direct billing available based on your coverage
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-[#7FFF00] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-slate-800">✓</span>
                  </div>
                  <p className="text-slate-600">
                    Convenient appointment communication by text when you opt in
                  </p>
                </div>
              </div>

              <div className="mt-8 p-4 bg-[#0047AB]/5 rounded-lg border border-[#0047AB]/10">
                <p className="text-sm text-slate-600">
                  SMS consent is optional and only applies if you choose to opt in.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#0047AB]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl font-semibold text-white mb-4">
            Book Your Visit with Muscle & Movement Rehab
          </h2>
          <p className="text-blue-100 mb-8 max-w-2xl mx-auto">
            Request your appointment today and choose how you would like us to contact you about your care.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="#sms-consent-form" 
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="inline-flex items-center justify-center h-12 px-8 bg-white text-[#0047AB] font-semibold rounded-full shadow-lg hover:bg-slate-50 transition-colors cursor-pointer"
              data-testid="cta-request-appointment"
            >
              Request Appointment
            </a>
            <a 
              href="tel:+16474903782" 
              className="inline-flex items-center justify-center h-12 px-8 bg-transparent text-white font-semibold rounded-full border-2 border-white hover:bg-white/10 transition-colors"
              data-testid="cta-call-us"
            >
              <Phone className="h-5 w-5 mr-2" />
              Call +1 647-490-3782
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
